import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AppBar, Toolbar, Typography, Box, Avatar, Button, useMediaQuery, useTheme, Menu, MenuItem, IconButton, Badge, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Stack, Snackbar, Divider, ListItemText } from '@mui/material';
import { Logout as LogoutIcon, Menu as MenuIcon, MoreVert, DarkMode, LightMode, Mail as MailIcon, PhotoCamera as PhotoCameraIcon, ManageAccounts as ManageAccountsIcon, Keyboard as KeyboardIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import { useAuth } from '../../services/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import { messagingService } from '../../services/MessagingService';
import { appRealtimeService } from '../../services/AppRealtimeService';
import type { CustomerPaidSocketPayload } from '../../services/AppRealtimeService';
import { warmRepairsListOnLogin } from '../../services/entitiesHybridService';
import {
  initMessageNotificationListeners,
  requestMessageNotificationPermission,
  setMessagesDialogOpenState,
  subscribeOpenMessagesDialog,
} from '../../services/messageNotificationService';
import { ensurePushSubscription } from '../../services/pushNotificationService';
import MessagesDialog from '../messages/MessagesDialog';
import { getVisibleKeyboardShortcuts } from '../../constants/appKeyboardShortcuts';
import { getPermissions } from '../../utils/permissions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../../firebaseConfig';
import { EmailAuthProvider, reauthenticateWithCredential, updateEmail, updatePassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import {
  addSystemUpdate,
  listSystemUpdates,
  markAllSystemUpdatesRead,
  markSystemUpdateRead,
  setSystemUpdatesUserScope,
  subscribeSystemUpdates,
  SystemUpdate,
} from '../../services/systemUpdatesStore';
import {
  knowledgeBaseService,
  KNOWLEDGE_BASE_ITEM_VIEWED_EVENT,
} from '../../services/KnowledgeBaseService';
import type { UnreadNewsItem } from '../../types/typesKnowledgeBase';
import { useNavigate } from 'react-router-dom';

const shortcutKeyChipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 34,
  px: 1.1,
  py: 0.45,
  borderRadius: '8px',
  bgcolor: '#fff',
  color: '#1a237e',
  fontWeight: 800,
  fontSize: '0.8rem',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  border: '1px solid #c5cae9',
  boxShadow: '0 2px 0 #9fa8da, 0 3px 6px rgba(26, 35, 126, 0.12)',
  lineHeight: 1.2,
  userSelect: 'none' as const,
};

interface AppHeaderProps {
  open: boolean;
  toggleDrawer: () => void;
}

const headerStyles = {
  appBar: (open: boolean) => ({
    background: 'linear-gradient(135deg,rgb(44, 146, 139) 0%,rgb(61, 97, 146) 100%)',
    color: '#ffffff',
    width: '100%',
    transition: (theme: Theme) => theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
    backdropFilter: 'blur(10px)',
  }),
  toolbar: (isMobile: boolean) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: { xs: 1, sm: 2 },
    paddingRight: { xs: 1, sm: 2 },
    minHeight: { xs: 56, sm: 64 }, // Smaller height on mobile
    gap: { xs: 0.5, sm: 2 },
  }),
  userInfoContainer: (isMobile: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.5, sm: 1.5 },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  }),
  userNameBox: (isMobile: boolean) => ({
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    borderRadius: { xs: 2, sm: 3 },
    padding: { xs: '4px 8px', sm: '6px 14px' },
    border: '1px solid rgba(255, 255, 255, 0.3)',
    maxWidth: { xs: 100, sm: 200 },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  }),
  userNameText: (isMobile: boolean) => ({
    fontWeight: 700,
    color: '#ffffff',
    fontSize: { xs: '0.75rem', sm: '1rem' },
  }),
  roleText: (isMobile: boolean) => ({
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
    userSelect: 'none',
    minWidth: { xs: 60, sm: 100 },
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: { xs: '0.625rem', sm: '0.875rem' },
    display: 'block',
  }),
  userActionsContainer: (isMobile: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.5, sm: 1 },
  }),
  avatar: (isMobile: boolean) => ({
    width: { xs: 32, sm: 40 },
    height: { xs: 32, sm: 40 },
    border: '2px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  }),
  logoutButton: (isMobile: boolean) => ({
    flexDirection: 'row-reverse',
    gap: 1,
    fontWeight: 600,
    color: '#ffffff',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: { xs: '8px', sm: '10px' },
    padding: { xs: '4px 12px', sm: '6px 16px' },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
      transform: 'translateY(-2px)',
    },
  }),
  mobileMenuButton: {
    color: '#ffffff',
    padding: { xs: '4px', sm: '8px' },
  },
  mobileMenu: {
    '& .MuiPaper-root': {
      marginTop: '8px',
      borderRadius: '12px',
      minWidth: '150px',
    },
  },
};

const roleHeaderLabels: Record<string, string> = {
  admin: 'מנהל 👑',
  service: 'שירות 👸',
  technician: 'טכנאי 🛠️',
  warehouse: 'מחסן 📦',
  field_agent: 'סוכן שטח 🚗',
  viewer: 'צופה 👁️',
  external_viewer: 'צפייה חיצונית 👁️',
};

const AppHeader: React.FC<AppHeaderProps> = React.memo(({ open, toggleDrawer }) => {
  const { user, logout, role, updateProfilePhoto } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const [userFullName, setUserFullName] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const visibleKeyboardShortcuts = React.useMemo(
    () => getVisibleKeyboardShortcuts(getPermissions(role).canEditServiceCalls),
    [role],
  );
  const canSeeCustomerPaidUpdates = role === 'admin' || role === 'service';
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  const [updatesAnchor, setUpdatesAnchor] = useState<null | HTMLElement>(null);
  const [systemUpdates, setSystemUpdates] = useState<SystemUpdate[]>([]);
  const unreadUpdatesCount = canSeeCustomerPaidUpdates
    ? systemUpdates.filter((item) => !item.read).length
    : 0;
  const [unreadNews, setUnreadNews] = useState<UnreadNewsItem[]>([]);
  const bellBadgeCount = unreadUpdatesCount + unreadNews.length;
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [messagesOpenContactId, setMessagesOpenContactId] = useState<string | null>(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountSuccess, setAccountSuccess] = useState<string | null>(null);
  const [customerPaidToast, setCustomerPaidToast] = useState<string | null>(null);
  const notifiedCustomerPaymentsRef = useRef<Set<string>>(new Set());
  const roleHeaderLabel = roleHeaderLabels[role || ''] || '👤 לא מוגדר';
  const todayLabel = new Date().toLocaleDateString('he-IL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  useEffect(() => {
    setUserFullName(user?.displayName || 'משתמש');
    setNewEmail(user?.email || '');
  }, [user]);

  useEffect(() => {
    setSystemUpdatesUserScope(user?.uid || null);
    setSystemUpdates(canSeeCustomerPaidUpdates ? listSystemUpdates() : []);
  }, [user?.uid, canSeeCustomerPaidUpdates]);

  useEffect(() => {
    return initMessageNotificationListeners();
  }, []);

  useEffect(() => {
    if (!user?.uid || !role || role === 'external_viewer') {
      setUnreadNews([]);
      return;
    }
    let cancelled = false;
    const loadUnreadNews = () => {
      knowledgeBaseService
        .getUnreadNews()
        .then((items) => {
          if (!cancelled) setUnreadNews(items);
        })
        .catch(() => {
          // הפעמון לא קריטי — נשאיר את הרשימה הקודמת
        });
    };
    loadUnreadNews();
    window.addEventListener('knowledge-base-news-changed', loadUnreadNews);
    window.addEventListener(KNOWLEDGE_BASE_ITEM_VIEWED_EVENT, loadUnreadNews);
    window.addEventListener('focus', loadUnreadNews);
    return () => {
      cancelled = true;
      window.removeEventListener('knowledge-base-news-changed', loadUnreadNews);
      window.removeEventListener(KNOWLEDGE_BASE_ITEM_VIEWED_EVENT, loadUnreadNews);
      window.removeEventListener('focus', loadUnreadNews);
    };
  }, [user?.uid, role]);

  useEffect(() => {
    if (!canSeeCustomerPaidUpdates) {
      setSystemUpdates([]);
      return;
    }
    return subscribeSystemUpdates(() => setSystemUpdates(listSystemUpdates()));
  }, [canSeeCustomerPaidUpdates]);

  useEffect(() => {
    if (!canSeeCustomerPaidUpdates) return;

    const onCustomerPaid = (event: Event) => {
      const detail = (event as CustomEvent<CustomerPaidSocketPayload>).detail || {};
      const callNumber = String(detail.callNumber || '').trim();
      const repairId = String(detail.repairId || '').trim();
      const amount = Number(detail.amount || 0);
      const dedupeKey = `${repairId || callNumber}|${detail.paidAt || ''}|${amount}`;
      if (!callNumber && !repairId) return;
      if (notifiedCustomerPaymentsRef.current.has(dedupeKey)) return;
      notifiedCustomerPaymentsRef.current.add(dedupeKey);
      const amountText =
        Number.isFinite(amount) && amount > 0
          ? `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : '';
      const toastText = callNumber
        ? `הלקוח שילם עבור קריאה ${callNumber}${amountText ? ` — ${amountText}` : ''}`
        : `הלקוח שילם עבור תיקון${amountText ? ` — ${amountText}` : ''}`;
      const saved = addSystemUpdate({
        id: dedupeKey,
        kind: 'customer_paid',
        title: 'הלקוח שילם',
        body: toastText,
        repairId: repairId || undefined,
        callNumber: callNumber || undefined,
        amount: Number.isFinite(amount) ? amount : undefined,
        at: detail.paidAt || new Date().toISOString(),
      });
      // לא להציג toast שוב אם העדכון כבר נקרא בעבר (אותו תשלום)
      if (!saved.read) {
        setCustomerPaidToast(toastText);
      }
      // לוג activity נכתב בשרת ב־applyVerifonePaymentToRepair — לא כאן (כדי שלא ייעלם כשאף מנהל לא מחובר)
    };
    window.addEventListener('repair-payment-customer-paid', onCustomerPaid as EventListener);
    return () => window.removeEventListener('repair-payment-customer-paid', onCustomerPaid as EventListener);
  }, [user, canSeeCustomerPaidUpdates]);

  useEffect(() => {
    setMessagesDialogOpenState(messagesDialogOpen);
  }, [messagesDialogOpen]);

  useEffect(() => {
    return subscribeOpenMessagesDialog((contactId) => {
      setMessagesOpenContactId(contactId);
      setMessagesDialogOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!user?.uid || !role || role === 'external_viewer') return;
    const timer = window.setTimeout(() => {
      void requestMessageNotificationPermission().then((permission) => {
        // הרשמה ל-Web Push — כך שהודעות יגיעו גם כשהטאב/הדפדפן סגורים לגמרי, לא רק כשהאפליקציה פתוחה.
        if (permission === 'granted') void ensurePushSubscription();
      });
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [user?.uid, role]);

  const openMessagesDialog = useCallback(() => {
    void requestMessageNotificationPermission().then((permission) => {
      if (permission === 'granted') void ensurePushSubscription();
    });
    setMessagesDialogOpen(true);
  }, []);

  const headerIconButtonSx = {
    color: '#ffffff',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '10px',
    padding: '6px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
  } as const;

  const updatesBadgeSx = {
    '& .MuiBadge-badge': {
      fontSize: '0.65rem',
      height: '18px',
      minWidth: '18px',
      fontWeight: 700,
    },
  };

  const openUpdatesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setUpdatesAnchor(event.currentTarget);
  };

  const closeUpdatesMenu = () => {
    // סגירת הרשימה = נצפה — לא להשאיר badge בכל התחברות מחדש
    markAllSystemUpdatesRead();
    setUpdatesAnchor(null);
  };

  const handleOpenNews = (item: UnreadNewsItem) => {
    setUnreadNews((prev) => prev.filter((news) => news.id !== item.id));
    closeUpdatesMenu();
    navigate(`/knowledge-base/${item.id}`);
  };

  const handleOpenUpdate = (item: SystemUpdate) => {
    markSystemUpdateRead(item.id);
    closeUpdatesMenu();
    if (item.callNumber) {
      navigate('/ServiceCallsPage', {
        state: {
          openRepairId: item.repairId || undefined,
          openCallNumber: item.callNumber || undefined,
        },
      });
    }
  };

  // Subscribe to unread messages count
  useEffect(() => {
    if (!user?.uid || !role || role === 'external_viewer') return;

    const unsubscribe = messagingService.subscribeToUnreadCount(
      user.uid,
      (count) => setUnreadCount(count)
    );

    return () => {
      unsubscribe();
    };
  }, [user?.uid, role]);

  useEffect(() => {
    const uid = String(user?.uid || '').trim();
    if (!uid || !role) {
      if (!uid) appRealtimeService.disconnect();
      return;
    }
    void appRealtimeService.connect(uid);
    if (role !== 'external_viewer') {
      warmRepairsListOnLogin();
    }
  }, [user?.uid, role]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('שגיאה בתהליך ההתנתקות:', error);
    }
    setMobileMenuAnchor(null);
  }, [logout]);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleAvatarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchor(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchor(null);
  };

  const handleUploadPhotoClick = () => {
    fileInputRef.current?.click();
    handleAvatarMenuClose();
  };

  const openAccountDialog = useCallback(() => {
    setAccountError(null);
    setAccountSuccess(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setNewEmail(user?.email || '');
    setAccountDialogOpen(true);
    handleAvatarMenuClose();
    handleMobileMenuClose();
  }, [user?.email]);

  const closeAccountDialog = useCallback(() => {
    if (accountSaving) return;
    setAccountDialogOpen(false);
  }, [accountSaving]);

  const handleSaveAccountSettings = useCallback(async () => {
    if (!user) return;
    setAccountError(null);
    setAccountSuccess(null);

    const emailChanged = String(newEmail || '').trim() !== String(user.email || '').trim();
    const passwordChanged = Boolean(String(newPassword || '').trim());

    if (!emailChanged && !passwordChanged) {
      setAccountError('לא זוהה שינוי לשמירה');
      return;
    }

    if (passwordChanged && String(newPassword).length < 6) {
      setAccountError('סיסמה חדשה חייבת להכיל לפחות 6 תווים');
      return;
    }
    if (passwordChanged && newPassword !== confirmPassword) {
      setAccountError('אימות הסיסמה החדשה לא תואם');
      return;
    }
    if (!currentPassword) {
      setAccountError('יש להזין סיסמה נוכחית לצורך אימות');
      return;
    }
    if (!user.email) {
      setAccountError('לא ניתן לעדכן פרטי כניסה למשתמש ללא אימייל');
      return;
    }

    try {
      setAccountSaving(true);
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      if (emailChanged) {
        const nextEmail = String(newEmail || '').trim();
        await updateEmail(user, nextEmail);
        const targetCollection = role === 'external_viewer' ? 'externalUsers' : 'users';
        await setDoc(doc(db, targetCollection, user.uid), { email: nextEmail }, { merge: true });
      }
      if (passwordChanged) {
        await updatePassword(user, newPassword);
      }

      setAccountSuccess('פרטי החשבון עודכנו בהצלחה');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const code = String(error?.code || '');
      if (code.includes('wrong-password') || code.includes('invalid-credential')) {
        setAccountError('הסיסמה הנוכחית שגויה');
      } else if (code.includes('email-already-in-use')) {
        setAccountError('האימייל כבר קיים במערכת');
      } else if (code.includes('requires-recent-login')) {
        setAccountError('נדרש להתחבר מחדש כדי לעדכן את פרטי החשבון');
      } else {
        setAccountError('שמירת ההגדרות נכשלה, נסה שוב');
      }
    } finally {
      setAccountSaving(false);
    }
  }, [confirmPassword, currentPassword, newEmail, newPassword, role, user]);

  const handlePhotoFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !user?.uid) return;
      if (!file.type.startsWith('image/')) {
        alert('נא לבחור קובץ תמונה');
        return;
      }
      try {
        setPhotoUploading(true);
        const path = `users/${user.uid}/avatar`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await updateProfilePhoto(url);
      } catch (err) {
        console.error('שגיאה בהעלאת תמונה:', err);
        alert('שגיאה בהעלאת התמונה. נסה שוב.');
      } finally {
        setPhotoUploading(false);
      }
    },
    [user?.uid, updateProfilePhoto]
  );

  return (
    <AppBar position="fixed" sx={headerStyles.appBar(open)}>
      <Toolbar sx={headerStyles.toolbar(isMobile)}>
        <Box sx={headerStyles.userInfoContainer(isMobile)}>
          {/* Menu button for mobile */}
          {isMobile && (
            <IconButton onClick={toggleDrawer} sx={headerStyles.mobileMenuButton}>
              <MenuIcon />
            </IconButton>
          )}

          <IconButton onClick={handleAvatarMenuOpen} disabled={photoUploading} sx={{ p: 0 }}>
            <Avatar 
              src={user?.photoURL || ''} 
              alt="User Avatar" 
              sx={headerStyles.avatar(isMobile)} 
            />
          </IconButton>
          
          <Box
            sx={{ ...headerStyles.userNameBox(isMobile), cursor: 'pointer' }}
            onClick={openAccountDialog}
            title="הגדרות חשבון"
          >
            <Typography variant="body1" noWrap sx={headerStyles.userNameText(isMobile)}>
              {userFullName}
            </Typography>
          </Box>
          {!isMobile && role !== 'external_viewer' && (
            <Box
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                px: 1.25,
                py: '6px',
                minHeight: 38,
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
              }}
            >
              <Typography variant="caption" noWrap sx={{ color: '#fff', fontWeight: 700 }}>
                תפקיד: {roleHeaderLabel}
              </Typography>
            </Box>
          )}

        </Box>

        <Box sx={headerStyles.userActionsContainer(isMobile)}>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handlePhotoFileChange}
            style={{ display: 'none' }}
            aria-hidden
          />
          {isMobile ? (
            <>
              {/* אייקון הודעות גם במובייל */}
              {role !== 'external_viewer' && (
                <>
                  <IconButton
                    onClick={openUpdatesMenu}
                    sx={{ color: '#ffffff', p: 0.5 }}
                    title="עדכונים"
                  >
                    <Badge badgeContent={bellBadgeCount} color="error" sx={updatesBadgeSx}>
                      <NotificationsIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                  <IconButton
                    onClick={openMessagesDialog}
                    sx={{ color: '#ffffff', p: 0.5 }}
                    title="הודעות"
                  >
                  <Badge 
                    badgeContent={unreadCount} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        height: '16px',
                        minWidth: '16px',
                        fontWeight: 700,
                      }
                    }}
                  >
                    <MailIcon fontSize="small" />
                  </Badge>
                </IconButton>
                </>
              )}
              <IconButton
                onClick={handleMobileMenuOpen}
                sx={headerStyles.mobileMenuButton}
              >
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={headerStyles.mobileMenu}
              >
                <MenuItem onClick={() => { setShortcutsDialogOpen(true); handleMobileMenuClose(); }}>
                  <KeyboardIcon sx={{ mr: 1 }} />
                  קיצורי מקלדת
                </MenuItem>
                <MenuItem onClick={() => { toggleMode(); handleMobileMenuClose(); }}>
                  {mode === 'light' ? <DarkMode sx={{ mr: 1 }} /> : <LightMode sx={{ mr: 1 }} />}
                  {mode === 'light' ? 'מצב כהה' : 'מצב בהיר'}
                </MenuItem>
                <MenuItem onClick={openAccountDialog}>
                  <ManageAccountsIcon sx={{ mr: 1 }} />
                  הגדרות חשבון
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  התנתק
                </MenuItem>
              </Menu>
              <Menu
                anchorEl={avatarMenuAnchor}
                open={Boolean(avatarMenuAnchor)}
                onClose={handleAvatarMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem onClick={handleUploadPhotoClick} disabled={photoUploading}>
                  <PhotoCameraIcon sx={{ mr: 1 }} />
                  {photoUploading ? 'מעלה...' : 'העלה תמונת פרופיל'}
                </MenuItem>
                <MenuItem onClick={openAccountDialog}>
                  <ManageAccountsIcon sx={{ mr: 1 }} />
                  הגדרות חשבון
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Box
                sx={{
                  color: '#ffffff',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  padding: '6px 12px',
                  minHeight: 38,
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                  📅 {todayLabel}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShortcutsDialogOpen(true)}
                sx={{
                  color: '#ffffff',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
                title="קיצורי מקלדת"
                aria-label="קיצורי מקלדת"
              >
                <KeyboardIcon />
              </IconButton>
              {/* Messages Icon - only for non-external viewers */}
              {role !== 'external_viewer' && (
                <>
                  <IconButton
                    onClick={openUpdatesMenu}
                    sx={headerIconButtonSx}
                    title="עדכונים"
                  >
                    <Badge badgeContent={bellBadgeCount} color="error" sx={updatesBadgeSx}>
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                  <IconButton
                    onClick={openMessagesDialog}
                    sx={headerIconButtonSx}
                    title="הודעות"
                  >
                  <Badge 
                    badgeContent={unreadCount} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: '18px',
                        minWidth: '18px',
                        fontWeight: 700,
                      }
                    }}
                  >
                    <MailIcon />
                  </Badge>
                </IconButton>
                </>
              )}
              
              <IconButton
                onClick={toggleMode}
                sx={{
                  color: '#ffffff',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
                title={mode === 'light' ? 'מעבר למצב כהה' : 'מעבר למצב בהיר'}
              >
                {mode === 'light' ? <DarkMode /> : <LightMode />}
              </IconButton>
              <Menu
                anchorEl={avatarMenuAnchor}
                open={Boolean(avatarMenuAnchor)}
                onClose={handleAvatarMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem onClick={handleUploadPhotoClick} disabled={photoUploading}>
                  <PhotoCameraIcon sx={{ mr: 1 }} />
                  {photoUploading ? 'מעלה...' : 'העלה תמונת פרופיל'}
                </MenuItem>
                <MenuItem onClick={openAccountDialog}>
                  <ManageAccountsIcon sx={{ mr: 1 }} />
                  הגדרות חשבון
                </MenuItem>
              </Menu>
              <Button
                variant="text"
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={headerStyles.logoutButton(isMobile)}
              >
                התנתק
              </Button>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Messages Dialog — לא זמין למשתמשים חיצוניים */}
      {role && role !== 'external_viewer' && (
      <MessagesDialog 
        open={messagesDialogOpen} 
        onClose={() => {
          setMessagesDialogOpen(false);
          setMessagesOpenContactId(null);
        }}
        initialContactId={messagesOpenContactId}
      />
      )}
      <Menu
        anchorEl={updatesAnchor}
        open={Boolean(updatesAnchor)}
        onClose={closeUpdatesMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { width: 360, maxWidth: 'calc(100vw - 24px)', mt: 1, direction: 'rtl' },
        }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>עדכונים</Typography>
          {unreadUpdatesCount > 0 && (
            <Button size="small" onClick={() => markAllSystemUpdatesRead()}>
              סמן הכל כנקרא
            </Button>
          )}
        </Box>
        <Divider />
        {unreadNews.map((news) => (
          <MenuItem
            key={`news-${news.id}`}
            onClick={() => handleOpenNews(news)}
            sx={{
              alignItems: 'flex-start',
              whiteSpace: 'normal',
              bgcolor:
                news.newsPriority === 'important'
                  ? 'rgba(237, 108, 2, 0.1)'
                  : 'rgba(25, 118, 210, 0.08)',
              py: 1.25,
            }}
          >
            <ListItemText
              primary={`📰 ${news.newsPriority === 'important' ? 'ידיעה חשובה' : 'ידיעה חדשה'}: ${news.title}`}
              secondary={[
                news.createdByName,
                news.createdAt ? new Date(news.createdAt).toLocaleDateString('he-IL') : '',
              ]
                .filter(Boolean)
                .join(' · ')}
              primaryTypographyProps={{ fontWeight: 800, fontSize: '0.9rem' }}
              secondaryTypographyProps={{ sx: { mt: 0.35 } }}
            />
          </MenuItem>
        ))}
        {unreadNews.length > 0 && systemUpdates.length > 0 && <Divider />}
        {systemUpdates.length === 0 && unreadNews.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="אין עדכונים עדיין" />
          </MenuItem>
        ) : (
          systemUpdates.slice(0, 30).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => handleOpenUpdate(item)}
              sx={{
                alignItems: 'flex-start',
                whiteSpace: 'normal',
                bgcolor: item.read ? 'transparent' : 'rgba(46, 125, 50, 0.08)',
                py: 1.25,
              }}
            >
              <ListItemText
                primary={item.title}
                secondary={item.body}
                primaryTypographyProps={{ fontWeight: item.read ? 600 : 800, fontSize: '0.9rem' }}
                secondaryTypographyProps={{ sx: { mt: 0.35 } }}
              />
            </MenuItem>
          ))
        )}
      </Menu>
      <Snackbar
        open={Boolean(customerPaidToast) && canSeeCustomerPaidUpdates}
        autoHideDuration={9000}
        onClose={() => setCustomerPaidToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCustomerPaidToast(null)}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
            fontWeight: 700,
            direction: 'rtl',
            gap: 1.25,
            '& .MuiAlert-icon': {
              margin: 0,
              marginInlineEnd: '10px',
              padding: 0,
            },
            '& .MuiAlert-message': { padding: 0, display: 'flex', alignItems: 'center' },
          }}
        >
          {customerPaidToast}
        </Alert>
      </Snackbar>
      <Dialog
        open={shortcutsDialogOpen}
        onClose={() => setShortcutsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>קיצורי מקלדת</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'rgba(227, 242, 253, 0.45)' }}>
          <Stack spacing={1.25}>
            {visibleKeyboardShortcuts.map((item) => (
              <Box
                key={item.keys.join('+')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  px: 1.5,
                  py: 1.15,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', textAlign: 'right' }}>
                  {item.action}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    direction: 'ltr',
                    flexShrink: 0,
                  }}
                >
                  {item.keys.map((keyLabel, index) => (
                    <React.Fragment key={`${item.action}-${keyLabel}-${index}`}>
                      {index > 0 && (
                        <Typography component="span" sx={{ fontWeight: 800, color: 'text.secondary', px: 0.15 }}>
                          +
                        </Typography>
                      )}
                      <Box component="kbd" sx={shortcutKeyChipSx}>
                        {keyLabel}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            הקיצורים לפי מיקום המקש במקלדת (עובדים גם במצב עברית). בחלק מהדפדפנים Alt+D עלול להתמקד בשורת הכתובת.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortcutsDialogOpen(false)}>סגור</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={accountDialogOpen} onClose={closeAccountDialog} fullWidth maxWidth="xs">
        <DialogTitle>הגדרות חשבון</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {accountError && <Alert severity="error" sx={{ mb: 1.5 }}>{accountError}</Alert>}
          {accountSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{accountSuccess}</Alert>}
          <TextField
            fullWidth
            margin="dense"
            label="אימייל חדש"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="סיסמה נוכחית (לאימות)"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="סיסמה חדשה"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="השאר ריק אם לא רוצים לשנות סיסמה"
          />
          <TextField
            fullWidth
            margin="dense"
            label="אימות סיסמה חדשה"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAccountDialog} disabled={accountSaving}>ביטול</Button>
          <Button onClick={handleSaveAccountSettings} variant="contained" disabled={accountSaving}>
            {accountSaving ? 'שומר...' : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
});

AppHeader.displayName = 'AppHeader';

export default AppHeader;
